import React, { useState, useEffect } from 'react';

import { Dropdown, FormInput_text, formInput_funcUtility, FormGroupRadio } from "components";

import { func_validation, func_currency, isNumberOrStringValid } from "functions";
import { chargeMethodList } from 'constants/lists';

export function useChargeInputs(props) {
    // Props
    const {
        isEditing,
        advancePaySetup,    
        isFormSubmissionAttempted,
    } = props;
    
    // Validate initial state values
    let unitPrice_init = func_validation.getObjectPropertyValue_validNumber(advancePaySetup, "UnitPrice", "0.00") + "";
    unitPrice_init = func_currency.validateStringDoublePrecisionCurrency(unitPrice_init, '0.00');
    let frequency_init = func_validation.getObjectPropertyValue_validNumber(advancePaySetup, "BillFrequence", 1);
    let quantity_init = func_validation.getObjectPropertyValue_validNumber(advancePaySetup, "Quantity", 0);
    let chargeMethod_init = func_validation.getObjectPropertyValue_stringTrimNotEmpty(advancePaySetup, "ChargeMethod", "Outlet Transaction");
    chargeMethod_init = chargeMethodList.find((obj) => obj?.label === chargeMethod_init)?.value;

    let subscriptionMethod_init = func_validation.getObjectPropertyValue_stringTrimNotEmpty(advancePaySetup, "SubscriptionMethod", "Monthly");
    let currencyCode_init = func_validation.getObjectPropertyValue_stringTrimNotEmpty(advancePaySetup, "CurrencyCode", "MYR");
    let currencyRate_init = func_validation.getObjectPropertyValue_validNumber(advancePaySetup, "CurrencyRate", 1) + "";
    currencyRate_init = func_currency.validateStringDoublePrecisionCurrency(currencyRate_init, '1.00');

    // States
    const [unitPrice, setUnitPrice] = useState(unitPrice_init);
    const [frequency, setFrequency] = useState(frequency_init);
    const [quantity, setQuantity] = useState(quantity_init);
    const [chargeMethod, setChargeMethod] = useState(chargeMethod_init);
    const [subscriptionMethod, setSubscriptionMethod] = useState(subscriptionMethod_init);
    const [currencyCode, setCurrencyCode] = useState(currencyCode_init);
    const [currencyRate, setCurrencyRate] = useState(currencyRate_init);
    const [isAnyInputChanged, setIsAnyInputChanged] = useState(false);

    // Check for changes in input from initial
    useEffect(() => {
        let isNotChangedFromOriginal = (
            unitPrice === unitPrice_init &&
            frequency === frequency_init &&
            quantity === quantity_init &&
            chargeMethod === chargeMethod_init &&
            subscriptionMethod === subscriptionMethod_init &&
            currencyCode === currencyCode_init &&
            currencyRate === currencyRate_init
        );

        let isChangedFromEmpty = (
            isNumberOrStringValid(unitPrice) ||
            isNumberOrStringValid(frequency) ||
            isNumberOrStringValid(quantity) ||
            chargeMethod ||
            subscriptionMethod ||
            currencyCode ||
            currencyRate
        )

        setIsAnyInputChanged(isEditing? !isNotChangedFromOriginal: isChangedFromEmpty);  
    }, [
        chargeMethod, subscriptionMethod,
        frequency, unitPrice, quantity,
        currencyCode, currencyRate
    ]);

    // Display
    let display = (
        <div className="input-wrapper__charge d-flex flex-wrap">

            {/* Charge Method */}
            <Dropdown
                className={"charge__charge-method"}
                label={"Charge Method"}
                isInputRequired

                selectedValueClassName={"charge-method__input"}

                options={[
                    {
                        label: "License",
                        value: "License"
                    },
                    {
                        label: "Outlet",
                        value: "Outlet"
                    },
                    {
                        label: "System",
                        value: "System"
                    },
                    {
                        label: "Outlet POS",
                        value: "OutletPos"
                    },
                    {
                        label: "Outlet Transaction",
                        value: "OutletTransaction"
                    },
                ]}
                valueFromParent={chargeMethod}
                selectedValue_default={chargeMethod}
                onChange={setChargeMethod}

                errorMsg={"Required"}
                customErrorValidation={isFormSubmissionAttempted && 
                    (func_validation.isStringTrimEmpty(chargeMethod) || func_validation.isNullUndefined(chargeMethod))}
                isFormSubmissionAttempted={isFormSubmissionAttempted}

                isSearchable
            />

            {/* Subscription Method */}
            <Dropdown
                className={"charge__subscription-method"}
                label={"Subscription Method"}
                isInputRequired

                selectedValueClassName={"subscription-method__input"}

                options={[
                    {
                        label: "Monthly",
                        value: "Monthly"
                    },
                    {
                        label: "Daily",
                        value: "Daily"
                    }
                ]}
                valueFromParent={subscriptionMethod}
                selectedValue_default={subscriptionMethod}
                onChange={setSubscriptionMethod}

                errorMsg={"Required"}
                isFormSubmissionAttempted={isFormSubmissionAttempted}

                isSearchable
                isInputValueTrim_onChange
            />

            {/* Frequency */}
            <Dropdown
                className={"charge__frequency"}
                label={"Bill Frequency"}
                isInputRequired

                selectedValueClassName={"frequency__input"}

                options={func_validation.convertArrayToDropdownArrayFormat([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12])}
                valueFromParent={frequency}
                selectedValue_default={frequency}
                onChange={setFrequency}

                errorMsg={"Required"}
                isFormSubmissionAttempted={isFormSubmissionAttempted}

                minInputDigit={1}
                minInputDigit_invalidMsg={"Bill Frequency must be at least 1"}
                isInputDigitOnly

                isSearchable
                isInputValueTrim_onChange 
            />

            {/* Unit Price */}
            <FormInput_text  
                inputLabel={"Unit Price"}

                className={"charge__unit-price"}
                inputValue_default={unitPrice} 

                handleInputBlurExtend={(value) => { 
                    if (func_validation.isStringTrimNotEmpty(value)) setUnitPrice(value);
                    else setUnitPrice("");
                }}

                inputMaxLength={64} 

                customErrorMsgDefault={"Required"}
                isFormSubmissionAttempted={isFormSubmissionAttempted} 

                handleInputFocusExtend={(e)=>{ formInput_funcUtility.handleInputFocus_onEnter_selectInput(e); }}
                isValuePropagationGuarded_onChange={(value)=>{ return (value.match(func_validation.getRegexMatchCurrencyDoublePrecisionCaseInsensitive()) || value == '') }}
                customValidator_onBlur={(value)=>{ return func_currency.validateStringDoublePrecisionCurrency(value, '0.00'); }}

                isInputValueTrim_onChange 
                isInputBlurHandlingRequired isInputValueTrim_onBlur
                isMarginBtmOptional
            />

            {/* Quantity */}
            <FormInput_text  
                inputLabel={"Quantity"}

                className={"charge__quantity"}
                inputValue_default={quantity} 

                isInputBlurHandlingRequired
                handleInputBlurExtend={(value) => { setQuantity(value); }}

                inputMaxLength={64}

                customErrorMsgDefault={"Required"}
                isFormSubmissionAttempted={isFormSubmissionAttempted}

                isInputValueTrim_onChange
                isMarginBtmOptional isInputDigitOnly isConvertedToNumber
            />

            {/* Currency Code */}
            <FormInput_text  
                inputLabel={"Currency Code"}

                className={"charge__currency-code"}
                inputValue_default={currencyCode} 

                isInputBlurHandlingRequired
                handleInputBlurExtend={(value) => { setCurrencyCode(value); }}

                inputMaxLength={3} 

                customErrorMsgDefault={"Required"}
                isFormSubmissionAttempted={isFormSubmissionAttempted} 

                isInputValueTrim_onChange
                isMarginBtmOptional
            />

            {/* Currency Rate */}
            <FormInput_text  
                inputLabel={"Currency Rate"}
                isInputOptional

                className={"charge__currency-rate"}
                inputValue_default={currencyRate} 

                isInputBlurHandlingRequired
                handleInputBlurExtend={(value) => { 
                    if (func_validation.isStringTrimNotEmpty(value)) setCurrencyRate(value);
                    else setCurrencyRate("");
                }}

                inputMaxLength={64} 

                handleInputFocusExtend={(e)=>{ formInput_funcUtility.handleInputFocus_onEnter_selectInput(e); }}
                isValuePropagationGuarded_onChange={(value)=>{ return (value.match(func_validation.getRegexMatchCurrencyDoublePrecisionCaseInsensitive()) || value == '') }}
                customValidator_onBlur={(value)=>{ return func_currency.validateStringDoublePrecisionCurrency(value, '1.00'); }}

                isInputValueTrim_onChange 
                isInputBlurHandlingRequired isInputValueTrim_onBlur
                isMarginBtmOptional isErrorMsgOptional
            />
        </div>
    );

    let isEveryInputValid = (
        func_validation.isStringTrimNotEmpty(chargeMethod) &&
        func_validation.isStringTrimNotEmpty(subscriptionMethod) &&
        func_validation.isNumberValid(frequency) &&
        (frequency > 0) &&
        func_validation.isStringTrimNotEmpty(unitPrice) &&
        func_validation.isNumberValid(quantity) &&
        // func_validation.isStringTrimNotEmpty(currencyRate) &&
        func_validation.isStringTrimNotEmpty(currencyCode)
    );

    let chargeDetails = {
        ChargeMethod: chargeMethod,
        SubscriptionMethod: subscriptionMethod,
        BillFrequence: frequency,
        UnitPrice: func_validation.getParsedFormattedFloat(unitPrice),
        Quantity: quantity,
        CurrencyCode: currencyCode,
        CurrencyRate: func_validation.isStringTrimNotEmpty(currencyRate)? func_validation.getParsedFormattedFloat(currencyRate):1,
    }

    return {
        chargeDetails,

        isEveryInputValid,
        isAnyInputChanged,
        display
    }
}